import React, { ReactElement, useEffect, useState } from "react";
import DialogModal from "../../../atoms/dialog";
import { Box, Card, Divider, Grid, List, ListItem, ListItemButton, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { useAgendaApi } from "./useAgendaApi";
import moment from "moment-timezone";
import { Add } from "@mui/icons-material";
import AgendaEditAndView from "./AgendaEditAndView";
import CustomBtnFilled from '../../../atoms/form-molecules/CustomBtnFilled';
import { getHeaderTitle } from "./helper";
import { isInternalUser } from "../../../utils/role";
import ScrollIndicatorWrapper from "../../../atoms/scroll-indicator-wrapper";

// Add interface for question structure used in UI
interface QuestionItem {
  question: string;
  expert_note_required: boolean;
}

interface projectAgendaInterface {
  isOpen: boolean;
  handleClose: () => void;
  projectId: string | null;
  agendaId?: string;
}
function ProjectAgenda({ isOpen, handleClose, projectId, agendaId }: projectAgendaInterface): ReactElement {
  const { fetchAgendas, isLoading: fetchAgendaLoading } = useAgendaApi();
  const { updateAgenda, isLoading: updateAgendaLoading } = useAgendaApi();
  const { createAgenda, isLoading: createAgendaLoading } = useAgendaApi();
  const isUserInternal = isInternalUser();
  const [projectAgenda, setProjectAgenda] = useState<any[]>([]);
  const [selectedAgendaId, setSelectedAgendaId] = useState<number | null>(agendaId ? parseInt(agendaId) : null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [titleError, setTitleError] = useState<string>('');

  // --- STATE CONSOLIDATION ---
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const [currentMode, setCurrentMode] = useState<'view' | 'edit' | 'preview' | 'create'>('view');

  const getAgendaList = async () => {
    if (projectId) {
      const agendas = await fetchAgendas(projectId, agendaId);
      setProjectAgenda(agendas);
    }
  };

  useEffect(() => {
    getAgendaList();
  }, []);

  const selectedAgenda = projectAgenda.find(agenda => agenda.id === selectedAgendaId);


  const handleAddAgenda = () => {
    setSelectedAgendaId(null);
    setIsCreatingNew(true);
    setCurrentMode('create');
    setCurrentTitle('');
    setTitleError('');
  };

  const validateTitle = (): boolean => {
    if (!currentTitle || currentTitle.trim().length === 0) {
      setTitleError('Title is required');
      return false;
    }
    if (currentTitle.length > 20) {
      setTitleError('Maximum 20 characters allowed');
      return false;
    }
    setTitleError('');
    return true;
  };

  const handleCreateSubmit = async (updatedQuestions: QuestionItem[]) => {
    if (!validateTitle()) {
      return;
    }
    if (updatedQuestions.length === 0) {
      return;
    }

    try {
      if (projectId) {
        await createAgenda(projectId, currentTitle, updatedQuestions);
        setIsCreatingNew(false);
        setCurrentMode('view');
        setCurrentTitle('');
        setTitleError('');
        await getAgendaList();
      }
    } catch (error) {
      console.error('Error creating agenda:', error);
    }
  };

  const handleUpdateSubmit = async (updatedQuestions: QuestionItem[], agendaId: any) => {
    if (!validateTitle()) {
      return;
    }
    if (updatedQuestions.length === 0) {
      return;
    }

    try {
      const res = await updateAgenda(agendaId, updatedQuestions, currentTitle);
      if (res && res.success) {
        setCurrentMode('view');
        await getAgendaList();
      }
    } catch (error) {
      console.error('Error updating agenda:', error);
    }
  };

  const handleModeChange = (mode: 'view' | 'edit' | 'preview' | 'create') => {
    if (mode === 'view') {
      setIsCreatingNew(false);
      setCurrentTitle(selectedAgenda?.title || '');
      setTitleError('');
    }
    setCurrentMode(mode);
  };

  const handleTitleChange = (title: string) => {
    setCurrentTitle(title);
    if (titleError) {
      setTitleError('');
    }
  };

  const handleAgendaSelect = (agendaId: number) => {
    const newSelectedAgenda = projectAgenda.find(a => a.id === agendaId);
    if (newSelectedAgenda) {
      setSelectedAgendaId(agendaId);
      setIsCreatingNew(false);
      setCurrentMode('view');
      setCurrentTitle(newSelectedAgenda.title || '');
      setTitleError('');
    }
  };

  return (
    <>
      <DialogModal
        isOpen={isOpen}
        handleClose={handleClose}
        title={'Project Agenda'}
        TitleEl={
          isUserInternal ? (
            <>
              <CustomBtnFilled
                label={"Add Agenda"}
                variant="contained"
                onClick={handleAddAgenda}
              >
                <Add />
              </CustomBtnFilled>
            </>
          ) : undefined
        }
      >
        <Grid container spacing={0} sx={{ flex: 1, height: '65vh', overflow: 'hidden', mt: '20px', width: '100%' }}>
          {/* Left Sidebar */}
          <Grid item xs={12} md={4} sx={{ height: '100%', paddingLeft: '0 !important', paddingTop: '0 !important' }}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
              <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" fontWeight={600}>
                  Agenda
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                  ({projectAgenda.length || 0})
                </Typography>
              </Box>
              <ScrollIndicatorWrapper>
                <List disablePadding sx={{ p: 0 }}>
                  {projectAgenda.map((agenda) => {
                    const questionCount = Array.isArray(agenda?.agenda?.questions)
                      ? agenda.agenda.questions.length
                      : 0;
                    const isPreviewActive = selectedAgendaId === agenda.id && !isCreatingNew;
                    const latestMoment = moment(agenda.updated_at);

                    const relativeTime = latestMoment ? latestMoment.fromNow() : "No timestamp";
                    return (
                      <React.Fragment key={agenda.id}>
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={isPreviewActive}
                            onClick={() => handleAgendaSelect(agenda.id)}
                            sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" justifyContent={'space-between'} alignItems="center" mt={0.5}>
                                  <Typography
                                    fontSize="14px"
                                    color={agenda.id === selectedAgendaId && !isCreatingNew ? 'primary' : 'text.primary'}
                                    fontWeight={500}
                                    noWrap
                                  >
                                    {agenda.title || "No Title Provided"}
                                  </Typography>
                                  <Box>
                                    <Typography fontSize="10px" color="text.secondary" whiteSpace="nowrap">
                                      {relativeTime}
                                    </Typography>
                                  </Box>
                                </Stack>
                              }
                              secondary={
                                <Stack direction="row" justifyContent={'space-between'} alignItems="center" mt={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    {questionCount} questions
                                  </Typography>
                                  <Typography fontSize="10px" color="text.secondary">
                                    {agenda.updatedBy.name}
                                  </Typography>
                                </Stack>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                        <Divider variant="middle" />
                      </React.Fragment>
                    );
                  })}
                </List>
              </ScrollIndicatorWrapper>
            </Card>
          </Grid>
          {/* Right Section */}
          <Grid item xs={12} md={8} sx={{ height: '100%', paddingLeft: '16px !important', paddingTop: '0 !important', backgroundColor: '#f5f4f4' }}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px', justifyContent: 'space-between' }}>
              <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                <Typography fontWeight={600} variant="h6">
                  {getHeaderTitle(currentMode)}
                </Typography>
                {(selectedAgenda || isCreatingNew) && (
                  <TextField
                    label={'Title'}
                    id="agenda-title"
                    disabled={currentMode === 'view' || currentMode === 'preview'}
                    value={currentTitle}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 20 }}
                    error={!!titleError || currentTitle.length > 20}
                    helperText={
                      titleError ||
                      (currentTitle.length > 20 ? "Maximum 20 characters allowed" : "")
                    }
                    required
                  />
                )}
              </Box>
              <AgendaEditAndView
                title={currentTitle}
                agendaData={isCreatingNew ? null : selectedAgenda?.agenda}
                isUserInternal={isUserInternal}
                onModeChange={handleModeChange}
                onSubmit={isCreatingNew ? handleCreateSubmit : handleUpdateSubmit}
                agendaId={selectedAgenda?.id}
                updateAgendaLoading={updateAgendaLoading}
                currentMode={currentMode}
                createAgendaLoading={createAgendaLoading}
                isCreatingNew={isCreatingNew}
                titleError={titleError}
              />
            </Card>
          </Grid>
        </Grid>
      </DialogModal>
    </>
  );
}

export default ProjectAgenda;